'use client'

import { useAtom } from 'jotai'
import { useCallback, useMemo } from 'react'

import { useLinkingGroupActions, useLinkingGroupState } from '@/hooks/use-linking-group-state'
import { resolveCardAccounts } from '@/lib/utils/account-select'
import { useUserAccounts } from '@/service/account.hook'
import { chartSelectedAccountIdAtomFamily } from '@/state/widget-instances'
import { TradeVenue } from '@/types/generated/common/types'

export interface CardAccountContext {
  tradeVenue?: TradeVenue
  /** "Viewing" identity: the tab's own pinned account if it still exists
   *  (any status), else any-status default for the venue. Use for
   *  read/display-only lookups (instrument metadata, position views) where
   *  showing data tied to a disconnected account is still meaningful. */
  accountId?: string
  /** Explicit name for `accountId`; kept alongside the legacy field during migration. */
  viewingAccountId?: string
  /** "Trading" identity: the tab's own pinned account ONLY if it's still
   *  order-eligible, else the venue's stable default eligible account (oldest
   *  `createdAt` — see resolveDefaultAccountId). Use for anything that submits an
   *  order/action. Never returns an inactive or read-only account. */
  tradingAccountId?: string
  /** What the user (or a tab pin) actually chose for trading on this card — never a derived
   *  default. This, not `tradingAccountId`, is the "card" tier of an order-account
   *  resolution: `tradingAccountId` always resolves via the venue default, so using it as
   *  the card tier would keep a remembered account from ever being reached. */
  cardTradingSelection?: { accountId: string; source: 'explicit' | 'tab-pinned' }
  /** The venue's derived default trading account, ignoring any selection or pin. */
  venueDefaultTradingAccountId?: string
}

/** Per-card account context. Prefers the account explicitly chosen via
 * ChartAccountSelector (chartSelectedAccountIdAtomFamily) — decoupled from
 * whichever watchlist tab happens to be focused, since some flows that open the
 * option chain never touch the chart's own tab for options (see
 * use-order-row-context-menu-actions.ts). Falls back to the card's focused
 * watch-list tab when nothing has been explicitly picked yet (or the pick is no
 * longer valid for the current venue), so widgets on different tabs/cards can
 * still independently default to different accounts/venues. */
export function useCardAccountContext(cardId?: string): CardAccountContext {
  const { focusedTab } = useLinkingGroupState(cardId ?? '')
  const { data } = useUserAccounts({ showDisabled: true })
  const accounts = data?.accounts ?? []
  const [explicitAccountId] = useAtom(chartSelectedAccountIdAtomFamily(cardId ?? ''))
  const resolved = resolveCardAccounts(focusedTab, explicitAccountId, accounts)
  const { tradeVenue, viewingAccountId: accountId, tradingAccountId } = resolved
  const venueDefaultTradingAccountId = resolved.venueDefaultTradingAccountId
  const selectionId = resolved.cardTradingSelection?.accountId
  const selectionSource = resolved.cardTradingSelection?.source

  // This hook is now called from dozens of components (every card + most global
  // chrome), many of them inside useMemo/useEffect/useCallback dependency arrays —
  // returning a fresh object literal every render made every one of those
  // recompute/re-render on every render of this hook's owner, not just when the
  // resolved account actually changes. Every field is a primitive (the selection is
  // rebuilt from its two primitives), so memoize on their values to give consumers a
  // stable reference across unrelated renders.
  return useMemo(
    () => ({
      tradeVenue,
      accountId,
      viewingAccountId: accountId,
      tradingAccountId,
      cardTradingSelection:
        selectionId && selectionSource
          ? { accountId: selectionId, source: selectionSource }
          : undefined,
      venueDefaultTradingAccountId,
    }),
    [
      tradeVenue,
      accountId,
      tradingAccountId,
      selectionId,
      selectionSource,
      venueDefaultTradingAccountId,
    ]
  )
}

/** Sets the card's explicit account selection (chartSelectedAccountIdAtomFamily) —
 * the value useCardAccountContext now prefers over focusedTab-based resolution —
 * and also pins the same accountId onto the card's focused watch-list tab, same as
 * before, so tab-scoped consumers (alignPageTabsToAccount, resolveTab*AccountId
 * fallback for OTHER cards/tabs) still see it. */
export function useSetCardAccountId(cardId?: string) {
  const { setWatchList } = useLinkingGroupActions(cardId ?? '')
  const [, setExplicitAccountId] = useAtom(chartSelectedAccountIdAtomFamily(cardId ?? ''))
  return useCallback(
    (accountId: string) => {
      setExplicitAccountId(accountId)
      setWatchList((prev) => {
        if (prev.selectedIndex < 0 || prev.selectedIndex >= prev.list.length) return prev
        if (prev.list[prev.selectedIndex].accountId === accountId) return prev
        return {
          ...prev,
          list: prev.list.map((item, i) =>
            i === prev.selectedIndex ? { ...item, accountId } : item
          ),
        }
      })
    },
    [setWatchList, setExplicitAccountId]
  )
}

/** Resolves "the card that currently matters" for global chrome that has no
 * cardId of its own (e.g. status-bar, hedge builder) — prefers
 * the last-focused card if it's still registered, else the first registered
 * one, else undefined (no cards on the page at all). */
export function resolveActiveCardId(
  lastFocusedCard: string | null,
  registeredContainers: Set<string> | string[]
): string | undefined {
  const ids =
    registeredContainers instanceof Set ? registeredContainers : new Set(registeredContainers)
  if (lastFocusedCard && ids.has(lastFocusedCard)) return lastFocusedCard
  return [...ids][0]
}
